import React, { useCallback, useEffect, useContext, useState, useMemo } from 'react'
import { MainButton } from '../../../Button/MainButton'
import { Dialog, DialogTitle, DialogContent, makeStyles } from '@material-ui/core'
import { FONT_WEIGHTS, TIME_BLOCKS } from '../../../../utils'
import BoardingWizardContext from '../BoardingWizardContext'
import PetAvatar from '../../../Common/PetAvatar'
import { FOOD_SCHEDULE_OPTIONS } from '../constants'
import CheckboxMenu from '../../../Common/Menus/CheckboxMenu'
import StyledAlert from '../../../Common/StyledAlert'

const EMPTY_SCHEDULE_ERROR = 'Select at least one meal time. Dogs must have at least one meal each day.'

const useStyles = makeStyles(() => ({
	content: { display: 'flex', flexDirection: 'column', gap: '1.4rem' },
	header: {
		marginBottom: '.8rem'
	},
	titleText: {
		fontWeight: FONT_WEIGHTS.ExtraBold,
		fontSize: '1rem',
		textTransform: 'uppercase'
	},
	checkboxMenuContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: '.5rem'
	},
	actionsContainer: {
		justifyContent: 'flex-start',
		marginTop: '1rem',
		display: 'flex',
		flexDirection: 'column',
		gap: '2rem',
		marginTop: '4rem'
	},
	mainActions: {
		display: 'flex',
		flexDirection: 'row',
		alignSelf: 'flex-start',
		gap: '1rem',
		'& .MuiButton-outlinedSizeLarge': {
			height: 'unset'
		}
	},
	sectionHeader: {
		fontFamily: 'Nunito',
		fontWeight: FONT_WEIGHTS.ExtraBold,
		fontSize: '0.75rem',
		textTransform: 'uppercase',
		letterSpacing: '0.047rem'
	}
}))

const EditMealAddOnsDialog = ({
	open,
	onSave,
	onCancel,
	title = 'Edit Food Schedule',
	values: { pet_id = '', name = '', image_url = '', meals = [], exceptions = [] },
	daycareFirstDay,
	daycareLastDay,
	hasEarlyDropoff,
	hasLatePickup
}) => {
	const classes = useStyles()
	const {
		wizardContextProvider: { wizard_state }
	} = useContext(BoardingWizardContext)

	const [editedMealAddons, setEditedMealAddons] = useState([])
	const [editedExceptions, setEditedExceptions] = useState([])

	useEffect(() => {
		if (!open) return
		setEditedMealAddons([...(meals || [])])
		setEditedExceptions([...(exceptions || [])])
	}, [open, meals, exceptions])

	const hasNoMealsSelected = editedMealAddons.length === 0

	const foodScheduleOptions = useMemo(() => {
		const options = FOOD_SCHEDULE_OPTIONS.map((option) => {
			const { title, time } = option
			let disabled = false

			let descriptionText = ''

			if (time === TIME_BLOCKS.Morning && !hasEarlyDropoff) {
				descriptionText = 'Not available on first day'
			}

			if (time === TIME_BLOCKS.Midday && !daycareFirstDay) {
				descriptionText = 'Not available on first day'
			}

			if (time === TIME_BLOCKS.Midday && !daycareLastDay) {
				descriptionText = 'Not available on last day'
			}

			if (time === TIME_BLOCKS.Midday && !daycareFirstDay && !daycareLastDay) {
				descriptionText = 'Not available on first or last day'
			}

			if (time === TIME_BLOCKS.Evening && !hasLatePickup) {
				descriptionText = 'Not available on last day'
			}

			const label = (
				<>
					<div>{title}</div>
					{descriptionText > '' ? <span>{descriptionText}</span> : null}
				</>
			)

			return { label, value: time, disabled }
		})
		return options
	}, [wizard_state, daycareFirstDay, daycareLastDay, hasEarlyDropoff, hasLatePickup])

	const handleEditedMealAddonsChanged = useCallback(
		(newAddons) => {
			setEditedMealAddons(newAddons)
		},
		[setEditedMealAddons]
	)

	const handleOnSave = useCallback(() => {
		if (editedMealAddons.length === 0) {
			return
		}
		if (onSave) {
			onSave({ pet_id, meals: editedMealAddons, exceptions: editedExceptions })
			setEditedMealAddons([])
		}
	}, [onSave, editedMealAddons, editedExceptions, pet_id, setEditedMealAddons])

	const handleOnCancel = useCallback(() => {
		setEditedMealAddons([...(meals || [])])
		setEditedExceptions([...(exceptions || [])])
		if (onCancel) {
			onCancel()
		}
	}, [onCancel, meals, exceptions])

	return (
		<Dialog
			PaperProps={{
				style: {
					width: 450,
					maxWidth: 450,
					minWidth: 450
				}
			}}
			maxWidth="md"
			open={open}
			onClose={() => null}>
			<DialogTitle>
				<div className={classes.header}>
					<div className={classes.titleText}>{title}</div>
				</div>
			</DialogTitle>
			<DialogContent className={classes.content}>
				<div>Choose the times this dog receives their daily meals.</div>
				<div style={{ margin: '0 0.09rem' }}>
					<PetAvatar petName={name} imageUrl={image_url} />
				</div>
				<div className={classes.checkboxMenuContainer}>
					<div className={classes.sectionHeader}>Timeblock</div>
					<CheckboxMenu
						key={`food-schedule-${pet_id}-${open ? 'open' : 'closed'}-${(meals || []).join(',')}`}
						selectedOptions={editedMealAddons}
						options={foodScheduleOptions}
						onSelectedChanged={handleEditedMealAddonsChanged}
					/>
				</div>
				{hasNoMealsSelected ? <StyledAlert type="error" text={EMPTY_SCHEDULE_ERROR} /> : null}
			</DialogContent>
			<div className={classes.actionsContainer}>
				<div className={classes.mainActions}>
					<MainButton
						style={{ paddingTop: '6px' }}
						variant="contained"
						label="Save"
						disabled={hasNoMealsSelected}
						onClick={handleOnSave}
					/>
					<MainButton label="Cancel" onClick={handleOnCancel} />
				</div>
			</div>
		</Dialog>
	)
}

export default EditMealAddOnsDialog
